import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

// Detects session expiry and redirects to the sign-in page.
//
// Three complementary mechanisms:
//
// 1. **ActionCable** (passive) — maintains a WebSocket subscription;
//    when the session expires the server rejects it and we redirect.
//
// 2. **Global fetch interceptor** — wraps window.fetch to catch 401
//    responses from any source (@rails/request.js, Turbo, etc.).
//
// 3. **TurboFailureApp** (server-side) — responds to Turbo requests
//    with a turbo_stream redirect action. This is handled entirely
//    server-side and requires no client code here.
//
// Usage (on body element):
//   data-controller="session-monitor"
//   data-session-monitor-sign-in-path-value="/accounts/sign_in"
export default class extends Controller {
  static values = { enabled: Boolean, mode: String, signInPath: String }

  connect() {
    if (!this.enabledValue) return

    this.redirecting = false
    this.setupCable()
    if (this.fullMode()) {
      this.setupFetchInterceptor()
    }
  }

  disconnect() {
    if (!this.enabledValue) return

    this.subscription?.unsubscribe()
    this.consumer?.disconnect()
    if (this.fullMode()) {
      this.removeFetchInterceptor()
    }
  }

  // -- ActionCable (passive detection) ------------------------------------

  setupCable() {
    this.consumer = createConsumer()

    this.subscription = this.consumer.subscriptions.create("SessionChannel", {
      connected: () => {
        this.startHeartbeat()
      },
      disconnected: () => {
        this.stopHeartbeat()
      },
      rejected: () => {
        this.redirectToSignIn()
      },
    })
  }

  startHeartbeat() {
    this.stopHeartbeat()
    this.heartbeatTimer = setInterval(() => {
      this.subscription?.perform("ping")
    }, 60000)
  }

  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  // -- Global fetch interceptor (active detection) ------------------------

  setupFetchInterceptor() {
    this.originalFetch = window.fetch

    window.fetch = (...args) => {
      // Once we know the session is gone, block all further requests
      // to prevent redirect loops and unnecessary server load.
      if (this.redirecting) {
        return Promise.reject(new Error("Session expired, redirecting"))
      }

      return this.originalFetch.apply(window, args).then((response) => {
        if (response.status === 401) {
          this.redirectToSignIn()
        }
        return response
      })
    }
  }

  removeFetchInterceptor() {
    if (this.originalFetch) {
      window.fetch = this.originalFetch
    }
  }

  // -- Redirect -----------------------------------------------------------

  redirectToSignIn() {
    if (!this.fullMode()) return
    if (this.redirecting) return
    this.redirecting = true

    const signInPath = this.signInPathValue || "/"
    const separator = signInPath.includes("?") ? "&" : "?"
    window.location.replace(`${signInPath}${separator}session_expired=true`)
  }

  fullMode() {
    return this.modeValue !== "presence"
  }
}
